import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Button,
  Menu,
  MenuItem,
  Divider,
  CircularProgress,
  IconButton,
} from '@mui/material';
import Moment from 'moment';
import { styled } from '@mui/material/styles';
import { Search as SearchIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import TopBackground from '../images/header_background.png';
import usersAPI from '../api/modules/users';
import { ClientUserModal } from './ClientUser/ClientUserModal';
import Icons from './icons';
import styledC from 'styled-components';
import {
  getSelectedCompany,
  selectUser,
} from '../redux/features/user/userSlice';
import { convertClientRolesToIds } from '../Helpers/clientRolesPermissions';
import { ClientOwnerModal } from './InviteClientOwner/ClientOwnerModal';
import SearchTableComponent from './SearchTableComponent';
import ShadowAccountExecutive from './AccountExecutiveInvite/ShadowAccountExecutive';
import { formatAccountPayload } from '../Helpers/formatters';
import TopContainer from './TopContainer/TopContainer';
import { TitleHeaderCol, TitleHeaderRow } from './Shared/TitleHeader/Index';
import {
  BlueAquaSearchButtonOutline,
  BodyContainer,
  BodyGridWrapper,
} from './Shared/BodyContainer/Index';
import AutoResizeGrid from './AutoResizeGrid/AutoResizeGrid';

const BlueAquaButtonFilled = styled(Button)({
  padding: '8px 22px',
  gap: '8px',
  width: 'auto',
  height: '42px',
  borderRadius: 0,
  textTransform: 'none',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '26px',
  fontSize: 16,
});

const StMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 0,
    width: '203px',
    '& .MuiMenu-list': {
      padding: '0px ',
    },
  },
}));

const StMenuItem = styled(MenuItem)(({ theme }) => ({
  fontFamily: 'Montserrat',
  '&:hover': {
    backgroundColor: 'rgba(239, 180, 71, 0.2)',
    fontWeight: 'bold',
  },
}));
const StDivider = styled(Divider)(({ theme }) => ({
  marginTop: '0px !important',
  marginBottom: '0px !important',
}));

const styles = {
  loadingContainer: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    padding: '60px 0',
  },
  imageContainer: {
    display: 'flex',
    height: '328px',
    backgroundImage: `url(${TopBackground})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  },
  dataGrid: {
    marginLeft: '38px',
    marginRight: '38px',
    marginTop: '2%',
    marginBottom: '4%',
    // maxWidth: '100%',
  },
  title: {
    textAlign: 'intial',
  },
  titleEnd: {
    textAlign: 'end',
  },
  filterButton: {
    height: '50px',
    width: '23%',
    left: '0px',
    top: '0px',
    borderRadius: '0px',
    fontFamily: 'Montserrat',
    fontWeight: 700,
    textTransform: 'unset !important',
    marginRight: '24px',
  },
  searchButton: {
    height: '50px',
    width: '50px',
    left: '0px',
    top: '0px',
    borderRadius: '0px',
  },
};

const ActionWrapper = styledC.div`
  display: flex;
  align-items: center;
`;

const columnsChooser = {
  CLIENTS: {},
  ACCOUNT_EXECUTIVE: {
    company: false,
  },
};

function AccountExecutiveDashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pageSize, setPageSize] = useState(25);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersCount, setUsersCount] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [textToSearch, setTextToSearch] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState(
    'CLIENT_ADMIN,CLIENT_STANDARD_USER'
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState();
  const [selectedDropdownFilter, setSelectedDropdownFilter] = useState(
    'CLIENT_ADMIN,CLIENT_STANDARD_USER'
  );

  const [clientUserModal, setClientUserModal] = useState({
    isOpen: false,
    selectedClient: null,
  });

  const [clientOwnerModal, setClientOwnerModal] = useState({
    isOpen: false,
    selectedClient: null,
  });

  const [shadowAccountExecutiveModal, setShadowAccountExecutiveModal] =
    useState({
      isOpen: false,
      selectedClient: null,
    });

  const userRedux = useSelector(selectUser);
  const selectedCompanyRedux = useSelector(getSelectedCompany);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClickInviteClientUser = () => {
    setClientUserModal({
      isOpen: true,
      selectedClient: null,
    });

    handleClose();
  };

  const handleCloseInviteClientOwner = () => {
    setClientOwnerModal({
      isOpen: false,
      selectedClient: null,
    });
  };

  const handleCloseInviteClientUser = () => {
    setClientUserModal({
      isOpen: false,
      selectedClient: null,
    });
  };

  const handleOpenShadowAccount = (id) => {
    setShadowAccountExecutiveModal({
      isOpen: true,
      selectedClient: id,
    });
  };

  const handleClickOpenEditClientOwner = (id) => {
    const userFound = users.find((user) => user.id === id);

    if (userFound) {
      const selectedClient = {
        ...userFound,
        accounts: userFound.accounts[0],
      };

      setClientOwnerModal({
        selectedClient,
        isOpen: true,
      });
    }
  };

  const handleClickOpenEditClientUser = (id) => {
    const userFound = users.find((user) => user.id === id);

    if (userFound) {
      const selectedClient = {
        ...userFound,
        accounts: userFound.accounts[0],
      };

      setClientUserModal({
        selectedClient,
        isOpen: true,
      });
    }
  };

  const renderAction = ({ row }) => {
    const role = row.role?.name;

    let action = undefined;

    if (role === 'ACCOUNT_EXECUTIVE') {
      return (
        <ActionWrapper>
          <IconButton
            title="Shadow Account"
            onClick={() => handleOpenShadowAccount(row.id)}
          >
            <Icons.UserIcon color="#9E9E9E" />
          </IconButton>
        </ActionWrapper>
      );
    }

    if (role === 'CLIENT_ACCOUNT_OWNER') {
      action = handleClickOpenEditClientOwner.bind(null, row.id);
    }

    if (role === 'CLIENT_ADMIN' || role === 'CLIENT_STANDARD_USER') {
      action = handleClickOpenEditClientUser.bind(null, row.id);
    }

    return (
      <ActionWrapper>
        <IconButton onClick={action}>
          <Icons.EditIcon />
        </IconButton>
        <IconButton>
          <Icons.ShareIcon />
        </IconButton>
      </ActionWrapper>
    );
  };

  const columns = [
    {
      field: 'company',
      headerName: 'Company',
      minWidth: 300,
      // maxWidth: 400,
      flex: 1,
      valueGetter: ({ row }) => {
        const { accounts } = row;

        if (!accounts?.length) return '-';

        const [account] = accounts;

        return account.company;
      },
    },
    {
      field: 'companyAddress',
      headerName: 'Company Address',
      // minWidth: 227,
      flex: 1,
      valueGetter: ({ row }) => {
        const { accounts } = row;

        if (!accounts?.length) return '-';

        const [account] = accounts;

        return account.companyAddress1;
      },
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      minWidth: 140,
      // maxWidth: 200,
      flex: 1,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      minWidth: 140,
      // maxWidth: 200,
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 300,
      // maxWidth: 600,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ color: '#B80A26' }}>{params.row.email}</Box>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      minWidth: 180,
      // maxWidth: 205,
      flex: 1,
      valueGetter: ({ row }) => {
        const { role } = row;

        return `${role?.name ? role.name.replace(/_/g, ' ') : '-'}`;
      },
    },
    {
      field: 'lastLogin',
      headerName: 'Last Login',
      minWidth: 120,
      // maxWidth: 140,
      renderCell: ({ row }) => {
        const value = row.lastLogin
          ? Moment(row.lastLogin).format('MM/DD/YYYY')
          : null;

        return (
          <Box
            sx={{
              color: value ? 'inherit' : '#EFB447',
              fontWeight: value ? 'inherit' : 700,
            }}
          >
            {value ? value : 'Unverified'}
          </Box>
        );
      },
    },
    {
      field: 'Action',
      headerName: 'Actions',
      align: 'left',
      minWidth: 180,
      maxWidth: 200,
      flex: 1,
      renderCell: renderAction,
    },
  ];

  const getUsersByRole = async (
    offset = 1,
    limit = 25,
    selectedCompany = [{ id: -1 }],
    role,
    search
  ) => {
    try {
      setIsLoading(true);

      const roles = convertClientRolesToIds(userRedux.roles, role.split(','));

      let getUsersPayload = {
        offset,
        limit,
        role: roles.join(','),
      };
      if (getUsersPayload.role === '') {
        getUsersPayload.role = '5,6';
      }

      if (search) Object.assign(getUsersPayload, { search });

      if (selectedCompany.length >= 1 && selectedCompany[0].id > -1) {
        const companyFilter = formatAccountPayload(selectedCompany);
        Object.assign(getUsersPayload, {
          accountId: companyFilter,
        });
      }

      const { data } = await usersAPI.getUsers(getUsersPayload);

      if (role === 'ACCOUNT_EXECUTIVE') {
        setColumnVisibilityModel(columnsChooser['ACCOUNT_EXECUTIVE']);
      } else {
        setColumnVisibilityModel(columnsChooser['CLIENTS']);
      }

      setUsers(data.users);
      setUsersCount(data.count);
    } catch (error) {
      console.log({ error });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReloadTable = async () => {
    await getUsersByRole(
      currentPage,
      pageSize,
      selectedCompanyRedux,
      selectedRoleFilter
    );
  };

  useEffect(() => {
    getUsersByRole(
      currentPage,
      pageSize,
      selectedCompanyRedux,
      selectedRoleFilter,
      isSearchOpen ? textToSearch : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, selectedCompanyRedux, selectedRoleFilter]);

  const onPageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const onPageChange = (page) => {
    setCurrentPage(page + 1);
  };

  const renderTable = useMemo(
    () => (
      <AutoResizeGrid
        sx={{ marginTop: '10px' }}
        rows={users}
        columns={columns}
        onPageChange={onPageChange}
        pageSize={pageSize}
        paginationMode="server"
        columnVisibilityModel={columnVisibilityModel}
        page={currentPage - 1}
        onPageSizeChange={onPageSizeChange}
        rowsPerPageOptions={[25, 50, 100]}
        disableSelectionOnClick
        pagination
        rowCount={usersCount}
        autoHeight
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPage, users, pageSize, usersCount, columnVisibilityModel]
  );

  const onCloseSearch = () => {
    setIsSearchOpen(false);
    setTextToSearch('');
    handleReloadTable();
  };

  const handleSearchUsers = (text, selectedValue) => {
    setTextToSearch(text);
    setSelectedDropdownFilter(selectedValue);

    getUsersByRole(
      currentPage,
      pageSize,
      selectedCompanyRedux,
      selectedValue,
      text
    );
  };

  const onOpenSearch = () => {
    setIsSearchOpen(true);
  };

  return (
    <Box
      sx={{ flexGrow: 1 }}
      style={{ minHeight: 'inherit', display: 'inherit' }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TopContainer>
            <TitleHeaderRow>
              <TitleHeaderCol>
                <Box
                  sx={{
                    fontFamily: 'OpenSans',
                    fontWeight: 400,
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    fontStyle: 'normal',
                    letterSpacing: '0.15px',
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                    }}
                  >
                    {`${userRedux.fullName}`}
                  </span>{' '}
                  - {`${userRedux.role?.name?.replace(/_/g, ' ') ?? ''}`}
                </Box>
                <Box
                  sx={{
                    width: '337px',
                    height: '65px',
                    fontFamily: 'OpenSans',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontSize: '48px',
                    lineHeight: '65px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Manage Users
                </Box>
              </TitleHeaderCol>
              <TitleHeaderCol>
                <Box>
                  <BlueAquaButtonFilled
                    id="basic-button"
                    aria-controls={open ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    variant={open ? 'contained' : 'blueGradient-contained'}
                    color={open ? 'customRed' : undefined}
                  >
                    Invite New User
                  </BlueAquaButtonFilled>
                  <StMenu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      'aria-labelledby': 'basic-button',
                    }}
                    sx={{
                      fontFamily: 'Montserrat',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      letterSspacing: '0.15px',
                      color: 'rgba(0, 0, 0, 0.54)',
                      borderRadius: '0 px !important',
                      paddingTop: '0px !important',
                    }}
                  >
                    <StDivider />
                    <StMenuItem onClick={onClickInviteClientUser}>
                      Client User
                    </StMenuItem>
                  </StMenu>
                </Box>
              </TitleHeaderCol>
            </TitleHeaderRow>

            {shadowAccountExecutiveModal.isOpen && (
              <ShadowAccountExecutive
                open={shadowAccountExecutiveModal.isOpen}
                clientId={shadowAccountExecutiveModal.selectedClient}
                onClose={() => {
                  setShadowAccountExecutiveModal({
                    isOpen: false,
                    selectedClient: null,
                  });
                }}
              />
            )}

            <ClientOwnerModal
              open={clientOwnerModal.isOpen}
              client={clientOwnerModal.selectedClient}
              onClose={handleCloseInviteClientOwner}
              onSubmit={handleReloadTable}
            />

            <ClientUserModal
              open={clientUserModal.isOpen}
              client={clientUserModal.selectedClient}
              onClose={handleCloseInviteClientUser}
              onSubmit={handleReloadTable}
            />
          </TopContainer>
        </Grid>

        <BodyGridWrapper item xs={12} md={12}>
          <BodyContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {!isSearchOpen ? (
                <>
                  <Box display="flex" width={'96%'}>
                    <Button
                      color="customRed"
                      variant={
                        selectedDropdownFilter ===
                        'CLIENT_ADMIN,CLIENT_STANDARD_USER'
                          ? 'contained'
                          : 'outlined'
                      }
                      sx={styles.filterButton}
                      onClick={() => {
                        setSelectedRoleFilter(
                          'CLIENT_ADMIN,CLIENT_STANDARD_USER'
                        );
                        setSelectedDropdownFilter(
                          'CLIENT_ADMIN,CLIENT_STANDARD_USER'
                        );
                      }}
                    >
                      Clients
                    </Button>
                    <BlueAquaSearchButtonOutline
                      onClick={onOpenSearch}
                      variant="blueGradient-outlined"
                    >
                      <SearchIcon fontSize={'large'} />
                    </BlueAquaSearchButtonOutline>
                  </Box>
                </>
              ) : (
                <SearchTableComponent
                  onCloseSearch={onCloseSearch}
                  onSubmitSearch={handleSearchUsers}
                  defaultValue={selectedDropdownFilter}
                  items={[
                    {
                      value: 'CLIENT_ADMIN,CLIENT_STANDARD_USER',
                      label: 'Clients',
                    },
                  ]}
                />
              )}
            </Box>

            {isLoading ? (
              <div style={styles.loadingContainer}>
                <CircularProgress style={{ color: '#B80A26' }} size={60} />
              </div>
            ) : (
              <div style={{ display: 'flex', height: '100%' }}>
                <div style={{ flexGrow: 1 }}>{renderTable}</div>
              </div>
            )}
          </BodyContainer>
        </BodyGridWrapper>
      </Grid>
    </Box>
  );
}

export default AccountExecutiveDashboard;
